import { useEffect, useState } from "react";
import { api, fmtDateTime, TASK_TYPE_LABEL, SYSTEM_TASK_TYPES } from "@/lib/api";
import { TaskTypeBadge, TransferTaskActions } from "@/components/TaskTypeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Lock, ArrowRight } from "lucide-react";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";

const PRIORITY_LABEL = { niedrig: "Niedrig", normal: "Normal", hoch: "Hoch", dringend: "Dringend" };
const PRIORITY_CLASS = { niedrig: "bg-slate-100 text-slate-700", normal: "bg-blue-50 text-blue-700", hoch: "bg-amber-50 text-amber-800", dringend: "bg-rose-50 text-rose-700" };
const TSTATUS_LABEL = { offen: "Offen", in_bearbeitung: "In Bearbeitung", blockiert: "Blockiert", erledigt: "Erledigt", abgebrochen: "Abgebrochen" };
const TSTATUS_CLASS = { offen: "bg-slate-100 text-slate-700", in_bearbeitung: "bg-blue-50 text-blue-700", blockiert: "bg-amber-50 text-amber-800", erledigt: "bg-emerald-50 text-emerald-700", abgebrochen: "bg-slate-100 text-slate-500" };
const NEW = { titel: "", beschreibung: "", assignee_user_id: "", priority: "normal", deadline: "", typ: "sonstiges", depends_on_task_ids: [] };

export default function TasksSection({ orderId, collapseKey }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(NEW);

  const load = async () => {
    const [t, u] = await Promise.all([api.get("/tasks", { params: { related_order_id: orderId } }), api.get("/team/users-lite")]);
    setTasks(t.data); setUsers(u.data);
  };
  useEffect(() => { load();  }, [orderId]);

  const create = async () => {
    if (!form.titel) { toast.error("Titel erforderlich."); return; }
    try {
      const body = { ...form, related_order_id: orderId };
      if (!body.deadline) delete body.deadline;
      if (!body.assignee_user_id) delete body.assignee_user_id;
      await api.post("/tasks", body);
      toast.success("Aufgabe angelegt.");
      setDialog(false); setForm(NEW); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const updateStatus = async (t, status) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Statuswechsel nicht möglich."); }
  };

  const toggleDep = (tid) => {
    const cur = form.depends_on_task_ids;
    setForm({ ...form, depends_on_task_ids: cur.includes(tid) ? cur.filter((x) => x !== tid) : [...cur, tid] });
  };

  return (
    <Card className="border-slate-200" data-testid="tasks-section">
      <CardContent className="p-5 space-y-4">
        <SectionHead k={collapseKey} label="Aufgaben" className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Aufgaben ({tasks.length})</h2>
          <SectionSummary k={collapseKey}>{`${tasks.filter((t) => !["erledigt", "abgebrochen"].includes(t.status)).length} offen`}</SectionSummary>
          <Button size="sm" className="ml-auto" onClick={() => { setForm(NEW); setDialog(true); }} data-testid="task-add-button"><Plus className="w-4 h-4 mr-1" /> Aufgabe</Button>
        </SectionHead>
        <SectionBody k={collapseKey}>
        {tasks.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Noch keine Aufgaben.</div>
        ) : (
          <div className="space-y-2" data-testid="tasks-list">
            {tasks.map((t) => {
              const hasOpenDeps = t.status === "offen" && (t.depends_on_task_ids || []).some((did) => {
                const dep = tasks.find((x) => x.id === did);
                return dep && dep.status !== "erledigt";
              });
              return (
                <div key={t.id} className="p-3 border rounded-md bg-white" data-testid={`task-${t.task_nummer}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-500">{t.task_nummer}</span>
                        <Badge className={PRIORITY_CLASS[t.priority] + " border-0"}>{PRIORITY_LABEL[t.priority]}</Badge>
                        <TaskTypeBadge typ={t.typ} testid={`task-typ-${t.task_nummer}`} />
                        {hasOpenDeps && <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Lock className="w-3 h-3" /> wartet auf Vorgänger</span>}
                      </div>
                      <div className="font-medium text-slate-900 mt-1">{t.titel}</div>
                      {t.beschreibung && <div className="text-xs text-slate-600 mt-0.5">{t.beschreibung}</div>}
                      <div className="text-xs text-slate-500 mt-1">
                        {t.assignee_name || "—"} {t.deadline && `· fällig ${fmtDateTime(t.deadline)}`}
                      </div>
                      {t.depends_on_task_ids?.length > 0 && (
                        <div className="text-xs text-slate-500 mt-1">Abhängig von: {t.depends_on_task_ids.map((d) => tasks.find((x) => x.id === d)?.task_nummer || "?").join(", ")}</div>
                      )}
                    </div>
                    <Select value={t.status} onValueChange={(v) => updateStatus(t, v)}>
                      <SelectTrigger className={`h-8 w-44 ${TSTATUS_CLASS[t.status]}`} data-testid={`task-status-${t.task_nummer}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(TSTATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </SectionBody>

        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent className="max-w-lg" data-testid="task-dialog">
            <DialogHeader><DialogTitle>Neue Aufgabe</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titel</Label><Input value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} data-testid="task-titel-input" /></div>
              <div><Label>Beschreibung</Label><Textarea rows={2} value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Typ</Label>
                  <Select value={form.typ} onValueChange={(v) => setForm({ ...form, typ: v })}>
                    <SelectTrigger data-testid="task-typ-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TASK_TYPE_LABEL).filter(([k]) => !SYSTEM_TASK_TYPES.includes(k)).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priorität</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PRIORITY_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Zugewiesen an</Label>
                  <Select value={form.assignee_user_id || "__none__"} onValueChange={(v) => setForm({ ...form, assignee_user_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— unassigned —</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.is_out_of_office ? " 🌴" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Deadline</Label>
                  <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="task-deadline-input" />
                </div>
              </div>
              {tasks.length > 0 && (
                <div>
                  <Label>Abhängig von (Vorgänger)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {tasks.map((t) => (
                      <button key={t.id} type="button" onClick={() => toggleDep(t.id)}
                        className={`text-xs px-2 py-1 rounded border ${form.depends_on_task_ids.includes(t.id) ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200"}`}>
                        {t.task_nummer}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
              <Button onClick={create} data-testid="task-save-button">Anlegen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
